import React, { useEffect, useState } from "react";
import toastr from "toastr";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Link,
  Box,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "../../Api/api";

export interface AMC {
  company_name: string;
  amc_code: number;
  amc_inc_date: string;
  amc_inc_years: number;
  totalaum: number;
  isin_count: number;
  isin_list: string[];
  logo?: string | null;
  fund_document_link? : string | null;
}

const FundOfferDocuments: React.FC = () => {
  const [amcs, setAmcs] = useState<AMC[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingFor, setSavingFor] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  const loadAMCs = async () => {
    try {
      setLoading(true);

      const response: any = await makeGETRequestOnUserService(
        "/mutual_fund/all_funds_companies/"
      );

      const data = response?.data?.data || [];

      setAmcs(data);

      const initialDrafts: Record<number, string> = {};

      data.forEach((amc: AMC) => {
        initialDrafts[amc.amc_code] =
          amc.fund_document_link || "";
      });

      setDrafts(initialDrafts);
    } catch (error) {
      toastr.error("Failed to load AMC data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAMCs();
  }, []);

  const isValidHttpUrl = (url: string) => {
    try {
      const parsed = new URL(url);

      return (
        parsed.protocol === "http:" ||
        parsed.protocol === "https:"
      );
    } catch {
      return false;
    }
  };

  const handleSave = async (amc: AMC) => {
    const url = (drafts[amc.amc_code] || "").trim();

    if (!url) {
      toastr.error("Please enter Fund Document URL.");
      return;
    }

    if (!isValidHttpUrl(url)) {
      toastr.error(
        "Please enter a valid URL starting with http:// or https://"
      );
      return;
    }

    try {
      setSavingFor(amc.amc_code);

      await makePOSTRequestOnUserService(
        "/mutual_fund/update_logo_and_fund_document_link_by_isin/",
        {
          isin_list: amc.isin_list,
          fund_document_link: url,
        }
      );

      toastr.success(
        `Document link updated for ${amc.company_name}`
      );

      loadAMCs();
    } catch (error) {
      toastr.error("Failed to update document link.");
    } finally {
      setSavingFor(null);
    }
  };

  const handleClear = async (amc: AMC) => {
    try {
      setSavingFor(amc.amc_code);

      await makePOSTRequestOnUserService(
        "/mutual_fund/update_logo_and_fund_document_link_by_isin/",
        {
          isin_list: amc.isin_list,
          fund_document_link: "",
        }
      );

      toastr.success(
        `Document link removed for ${amc.company_name}`
      );

      loadAMCs();
    } catch (error) {
      toastr.error("Failed to remove document link.");
    } finally {
      setSavingFor(null);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        p={5}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Fund Offer Documents Management
      </Typography>

      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: 20 }}
      >
        Configure one Fund Offer Documents URL for each AMC.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Fund Name</strong>
              </TableCell>

              <TableCell>
                <strong>Total Funds</strong>
              </TableCell>

              <TableCell>
                <strong>Current Link</strong>
              </TableCell>

              <TableCell width="40%">
                <strong>Fund Document URL</strong>
              </TableCell>

              <TableCell align="center">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {amcs.map((amc) => {
              const isSaving =
                savingFor === amc.amc_code;

              return (
                <TableRow key={amc.amc_code}>
                  <TableCell>
                    <Box
                      display="flex"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="subtitle2">
                          {amc.company_name}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          AMC Code: {amc.amc_code}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    {amc.isin_count ||
                      amc.isin_list?.length ||
                      0}
                  </TableCell>

                  <TableCell>
                    {amc.fund_document_link ? (
                      <Link
                        href={
                          amc.fund_document_link
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Link
                      </Link>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                      >
                        Not Configured
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="https://example.com/document"
                      value={
                        drafts[amc.amc_code] || ""
                      }
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [amc.amc_code]:
                            e.target.value,
                        }))
                      }
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={isSaving}
                      onClick={() =>
                        handleSave(amc)
                      }
                      style={{ marginRight: 8 }}
                    >
                      {isSaving
                        ? "Saving..."
                        : "Save"}
                    </Button>

<Button
  variant="outlined"
  size="small"
  onClick={() => handleClear(amc)}
>
  <DeleteOutlineIcon />
</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default FundOfferDocuments;